import React from "react";
import { Alert, Autocomplete, Box, Button, Chip, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageContainer, SectionHeader, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import { gs, Loader, StaleHint, ensureOptions, ensureRegions, regionLabel, regionOptions, planText, canEdit, Cut, UsageBar, FIXED, ELLIPSIS,
         ago, when, n, usdH, mb } from "./servers";

// Game Servers → Fleets and Machine Specs: the two operator tables the planner reads (§10). A fleet sizes a pooled
// profile per branch and region (`servers fleets`, `fleet-add` / `fleet-update` / `fleet-remove`); a spec says what a
// Vultr plan carries and costs (`servers specs`, `spec-add` / `spec-update` / `spec-remove`). Both read `servers
// options` for their dropdowns (branches, profiles, regions, plans).

const int = (v) => { const x = parseInt(v, 10); return Number.isFinite(x) ? x : NaN; };
const actionsCol = (onEdit, onRemove) => ({ id: "actions", label: "", width: 80, render: r => <Box sx={{ display: "flex", gap: 0.25 }}>
    <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
    <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => onRemove(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
</Box> });
const updatedCol = { id: "updated", label: "Updated", width: 120, render: r => <Tooltip title={`${when(r.updatedDate)}${r.updatedBy ? ` by ${r.updatedBy}` : ""}`}>
    <Box sx={ELLIPSIS}>{ago(r.updatedDate)}{r.updatedBy ? ` · ${r.updatedBy}` : ""}</Box></Tooltip> };

// ===================== Fleets =====================
export class FleetsPage extends React.Component {
    state = { fleets: null, options: null, optionsError: null, netRegions: null, loadedAt: null, stale: null, dialog: null, busy: false };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        ensureRegions(this);
        const r = await gs("Loading fleets", ["fleets"], { silent: true });
        return { fleets: r?.fleets ?? [] };
    });

    componentDidMount() {
        this.loader.start();
    }
    componentWillUnmount() { this.loader.stop(); }

    pooled = () => (this.state.options?.profiles ?? []).filter(p => String(p.mode).toLowerCase() === "pooled");
    profile = (id) => (this.state.options?.profiles ?? []).find(p => p.id === id);

    openAdd = () => {
        const o = this.state.options;
        const branches = o?.branches ?? [];
        this.setState({ dialog: { mode: "add", branch: branches.includes("Development") ? "Development" : (branches[0] ?? ""),
            profile: this.pooled()[0]?.id ?? "", region: this.state.netRegions?.defaultRegion ?? o?.defaultRegion ?? "", warmSlots: "2", minServers: "0", maxServers: "4" } });
    };
    openEdit = (f) => this.setState({ dialog: { mode: "edit", id: f.id, branch: f.branch, profile: f.profile, region: f.region,
        warmSlots: String(f.warmSlots ?? 0), minServers: String(f.minServers ?? 0), maxServers: String(f.maxServers ?? 1) } });
    setDialog = (patch) => this.setState(st => ({ dialog: { ...st.dialog, ...patch } }));

    dialogError = () => {
        const d = this.state.dialog;
        const warm = int(d.warmSlots), min = int(d.minServers), max = int(d.maxServers);
        if (d.mode === "add" && (!d.branch || !d.profile || !d.region)) return "Branch, profile and region are required.";
        if (!(warm >= 0) || !(min >= 0)) return "Warm slots and min servers must be ≥ 0.";
        if (!(max >= 1) || max < min) return "Max servers must be ≥ 1 and ≥ min servers.";
        return "";
    };
    save = () => {
        const d = this.state.dialog;
        const nums = { warmSlots: int(d.warmSlots), minServers: int(d.minServers), maxServers: int(d.maxServers) };
        const [desc, cmd, payload] = d.mode === "add"
            ? ["Adding the fleet", "fleet-add", { branch: d.branch, profile: d.profile, region: d.region, ...nums }]
            : ["Saving the fleet", "fleet-update", { id: d.id, ...nums }];
        this.setState({ busy: true });
        gs(desc, [cmd], { payload, retry: false })
            .then(r => { if (r?.fleets) this.setState({ fleets: r.fleets }); this.setState({ dialog: null }); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };
    remove = (f) => {
        const p = this.profile(f.profile);
        if (!window.confirm(`Remove the fleet ${f.branch} · ${f.profile} · ${f.region}?\n\nNothing stops at once: its ${f.servers ?? 0} servers drain once idle for ${p?.idleDrainMin ?? "the profile's"} minutes, and no new ones are placed for it.`)) return;
        gs("Removing the fleet", ["fleet-remove", f.id])
            .then(r => { if (r?.fleets) this.setState({ fleets: r.fleets }); })
            .catch(() => {});
    };

    // What the planner aims at for these numbers (§10): max(min, servers for warmSlots free slots), capped by max
    targetText = (d) => {
        const p = this.profile(d.profile);
        const warm = int(d.warmSlots), min = int(d.minServers), max = int(d.maxServers);
        if (!p || !(warm >= 0) || !(min >= 0) || !(max >= 1)) return null;
        const per = Math.max(1, p.maxSessions ?? 1);
        const forWarm = Math.ceil(warm / per);
        return `With no sessions running: ${Math.min(max, Math.max(min, forWarm))} server(s) of ${p.id} (${per} sessions each, ${p.weight ?? 1} capacity unit(s) each). ` +
               `As sessions claim slots, servers are added to keep ${warm} slot(s) free, up to ${max} server(s).`;
    };

    renderDialog() {
        const { dialog: d, options, netRegions, busy } = this.state;
        if (!d) return null;
        const err = this.dialogError();
        const editing = d.mode === "edit";
        const target = this.targetText(d);
        const p = this.profile(d.profile);
        return (
            <FormDialog open title={editing ? `Edit fleet ${d.branch} · ${d.profile} · ${d.region}` : "Add a fleet"}
                        onClose={() => this.setState({ dialog: null })} onConfirm={this.save}
                        confirmText={editing ? "Save" : "Add"} confirmDisabled={!!err || busy} maxWidth="sm" fullWidth>
                {!options && <Alert severity={this.state.optionsError ? "error" : "info"} sx={{ mb: 1 }}>{this.state.optionsError ? `Could not load the branches, profiles and regions: ${this.state.optionsError}` : "Loading the branches, profiles and regions…"}</Alert>}
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <SelectField label="Branch" fullWidth value={d.branch} disabled={editing} onChange={v => this.setDialog({ branch: v })}
                                 options={editing ? [d.branch] : (options?.branches ?? [])} />
                    <SelectField label="Profile (pooled)" fullWidth value={d.profile} disabled={editing} onChange={v => this.setDialog({ profile: v })}
                                 options={editing ? [d.profile] : this.pooled().map(x => ({ value: x.id, label: `${x.id} · ${x.maxSessions} sessions × ${x.maxPlayers} players` }))} />
                </Box>
                <SelectField label="Region" fullWidth sx={{ mt: 2 }} value={d.region} disabled={editing} onChange={v => this.setDialog({ region: v })}
                             options={editing ? [{ value: d.region, label: regionLabel(d.region, netRegions) }] : regionOptions(netRegions, ["local", d.region], { enabledOnly: true })} />
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <TextField label="Warm slots" type="number" fullWidth value={d.warmSlots} onChange={e => this.setDialog({ warmSlots: e.target.value })} slotProps={{ htmlInput: { min: 0 } }} />
                    <TextField label="Min servers" type="number" fullWidth value={d.minServers} onChange={e => this.setDialog({ minServers: e.target.value })} slotProps={{ htmlInput: { min: 0 } }} />
                    <TextField label="Max servers" type="number" fullWidth value={d.maxServers} onChange={e => this.setDialog({ maxServers: e.target.value })} slotProps={{ htmlInput: { min: 1 } }} />
                </Box>
                {err && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>{err}</Typography>}
                {!err && target && <Typography variant="body2" sx={{ mt: 1.5 }}>{target}</Typography>}
                {p && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Profile {p.id}: <code>{p.executable} {(p.args ?? []).join(" ")}</code>, {p.ports} port(s), drains after {p.idleDrainMin} idle minutes when no fleet owns it.
                </Typography>}
                {!editing && this.pooled().length === 0 && options && <Alert severity="warning" sx={{ mt: 1 }}>This title declares no pooled profile (GameServersConfig.profiles).</Alert>}
            </FormDialog>
        );
    }

    render() {
        const { fleets, netRegions, loadedAt, stale } = this.state;
        const edit = canEdit();
        const columns = [
            { id: "branch", label: "Branch", width: 105, render: f => f.branch },
            { id: "profile", label: "Profile", width: 115, sx: ELLIPSIS, render: f => f.profile },
            { id: "region", label: "Region", width: 150, render: f => <Cut text={regionLabel(f.region, netRegions)} /> },
            { id: "warmSlots", label: "Warm slots", width: 80, align: "right", render: f => n(f.warmSlots) },
            { id: "min", label: "Min", width: 55, align: "right", render: f => n(f.minServers) },
            { id: "max", label: "Max", width: 55, align: "right", render: f => n(f.maxServers) },
            { id: "servers", label: "Servers ready / total", width: 110, align: "right", render: f => `${n(f.ready)} / ${n(f.servers)}` },
            { id: "slots", label: "Slots used / total", width: 115, align: "right", render: f => <>{n(f.slotsUsed)} / {n(f.slots)}<UsageBar used={f.slotsUsed} total={f.slots} /></> },
            { id: "status", label: "Warm now", width: 140, render: f => {
                const free = Math.max(0, (f.slots ?? 0) - (f.slotsUsed ?? 0));
                const atMax = (f.servers ?? 0) >= (f.maxServers ?? 0);
                if (free >= (f.warmSlots ?? 0)) return <Chip size="small" color="success" variant="outlined" label={`${free} free · ok`} />;
                return <Tooltip title={atMax ? "At max servers: raise max to keep the warm slots" : "Below the warm target: the planner is placing servers (or no machine has room)"}>
                    <Chip size="small" color={atMax ? "error" : "warning"} label={`${free} / ${f.warmSlots} free${atMax ? " · max" : ""}`} />
                </Tooltip>;
            } },
            updatedCol,
            ...(edit ? [actionsCol(this.openEdit, this.remove)] : []),
        ];
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Fleets">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    {edit && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openAdd}>Add fleet</Button>}
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2">
                        A fleet keeps one <strong>pooled</strong> profile (matches, rooms) of a branch running in a region. Its target is
                        <strong> max(min servers, the servers needed for warm slots free session slots)</strong>, capped by max servers.
                        <strong> Warm slots</strong> are free session slots kept ready on the branch's newest build, so a new match or room opens
                        at once instead of waiting for a server to boot; as sessions claim them the planner places more servers (when a machine
                        has room). A server above the target drains once it has been idle for 5 minutes. Persistent profiles (worlds) are not
                        sized here: each world gets its own server. Older major.minors are not kept warm: they start on demand.
                    </Typography>
                </Paper>
                {fleets == null
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the fleets: ${stale}` : "Loading…"}</Typography>
                    : fleets.length === 0
                        ? <Typography variant="body2" color="text.secondary">No fleet: no pooled server is kept warm (sessions still start servers on demand when a machine has room).</Typography>
                        : <DataTable columns={columns} rows={fleets} getRowKey={f => f.id} sx={FIXED} />}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Servers, ready and slots count every version of the fleet's branch, profile and region; slots count the servers that take sessions (desired run).
                </Typography>
                {this.renderDialog()}
            </PageContainer>
        );
    }
}

// ===================== Machine Specs =====================
export class SpecsPage extends React.Component {
    state = { specs: null, options: null, optionsError: null, loadedAt: null, stale: null, dialog: null, busy: false };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        const r = await gs("Loading specs", ["specs"], { silent: true });
        return { specs: r?.specs ?? [] };
    }, 30000);

    componentDidMount() {
        this.loader.start();
    }
    componentWillUnmount() { this.loader.stop(); }

    plan = (id) => (this.state.options?.plans ?? []).find(p => p.id === id);

    openAdd = () => this.setState({ dialog: { mode: "add", plan: "", capacity: "", hourlyUsd: "", note: "" } });
    openEdit = (s) => this.setState({ dialog: { mode: "edit", id: s.id, plan: s.plan, capacity: String(s.capacity ?? ""), hourlyUsd: String(s.hourlyUsd ?? ""), note: s.note ?? "" } });
    setDialog = (patch) => this.setState(st => ({ dialog: { ...st.dialog, ...patch } }));
    dialogError = () => {
        const d = this.state.dialog;
        if (!d.plan.trim()) return "Pick a plan.";
        if (!(int(d.capacity) > 0)) return "Capacity (units) must be > 0.";
        if (String(d.hourlyUsd).trim() !== "" && !(parseFloat(d.hourlyUsd) >= 0)) return "The hourly price must be ≥ 0 (0 = Vultr's price).";
        return "";
    };
    save = () => {
        const d = this.state.dialog;
        // 0 = Vultr's price on both commands; on an edit an empty field leaves the price as it is
        const empty = String(d.hourlyUsd).trim() === "";
        const hourly = empty ? 0 : parseFloat(d.hourlyUsd);
        const [desc, cmd, payload] = d.mode === "add"
            ? ["Adding the spec", "spec-add", { plan: d.plan.trim(), capacity: int(d.capacity), hourlyUsd: hourly, note: d.note || undefined }]
            : ["Saving the spec", "spec-update", { id: d.id, capacity: int(d.capacity), ...(empty ? {} : { hourlyUsd: hourly }), note: d.note }];
        this.setState({ busy: true });
        gs(desc, [cmd], { payload, retry: false })
            .then(r => { if (r?.specs) this.setState({ specs: r.specs }); this.setState({ dialog: null }); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };
    remove = (s) => {
        if (!window.confirm(`Remove the spec of plan ${s.plan}?\n\nIts ${s.machines ?? 0} machine(s) keep their capacity and price; the plan can no longer be deployed until it has a spec again.`)) return;
        gs("Removing the spec", ["spec-remove", s.id])
            .then(r => { if (r?.specs) this.setState({ specs: r.specs }); })
            .catch(() => {});
    };

    renderDialog() {
        const { dialog: d, options, optionsError, specs, busy } = this.state;
        if (!d) return null;
        const err = this.dialogError();
        const editing = d.mode === "edit";
        const taken = new Set((specs ?? []).map(s => s.plan));
        const plans = (options?.plans ?? []).filter(p => !taken.has(p.id))
            .sort((a, b) => String(a.type).localeCompare(String(b.type)) || (a.monthlyUsd ?? 0) - (b.monthlyUsd ?? 0));
        const vultr = this.plan(d.plan);
        const profiles = options?.profiles ?? [];
        return (
            <FormDialog open title={editing ? `Edit the spec of ${d.plan}` : "Add a machine spec"} onClose={() => this.setState({ dialog: null })} onConfirm={this.save}
                        confirmText={editing ? "Save" : "Add"} confirmDisabled={!!err || busy} maxWidth="sm" fullWidth>
                {(optionsError || options?.vultrError) && <Alert severity="warning" sx={{ mb: 1 }}>Vultr's plan list is unavailable ({optionsError || options.vultrError}): type the plan id.</Alert>}
                {editing
                    ? <TextField label="Plan" fullWidth sx={{ mt: 1 }} value={d.plan} disabled />
                    : plans.length > 0
                        ? <Autocomplete sx={{ mt: 1 }} options={plans} value={plans.find(p => p.id === d.plan) ?? null}
                                        onChange={(_, p) => this.setDialog({ plan: p?.id ?? "" })}
                                        getOptionLabel={p => `${p.id} — ${planText(p)}`} groupBy={p => p.type ?? ""}
                                        isOptionEqualToValue={(a, b) => a.id === b.id}
                                        renderInput={params => <TextField {...params} label="Plan (Vultr)" />} />
                        : <TextField label="Plan id (e.g. vc2-2c-4gb)" fullWidth sx={{ mt: 1 }} value={d.plan} onChange={e => this.setDialog({ plan: e.target.value })} />}
                {vultr && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {planText(vultr)} · {(vultr.locations ?? []).length} regions
                </Typography>}
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <TextField label="Capacity (units)" type="number" fullWidth value={d.capacity} onChange={e => this.setDialog({ capacity: e.target.value })} slotProps={{ htmlInput: { min: 1 } }} />
                    <Box sx={{ flex: 1 }}>
                        <TextField label="Hourly price (USD)" type="number" fullWidth value={d.hourlyUsd} onChange={e => this.setDialog({ hourlyUsd: e.target.value })}
                                   helperText={`0 = Vultr's price${vultr ? ` ($${Number(vultr.hourlyUsd ?? 0).toFixed(3)}/h)` : ""}${editing ? " · empty = unchanged" : " · empty = Vultr's"}`}
                                   slotProps={{ htmlInput: { min: 0, step: 0.001 } }} />
                        {editing && <Button size="small" sx={{ mt: 0.5, px: 0.5, minWidth: 0 }} disabled={String(d.hourlyUsd).trim() === "0"}
                                            onClick={() => this.setDialog({ hourlyUsd: "0" })}>Use Vultr's price</Button>}
                    </Box>
                </Box>
                <TextField label="Note" fullWidth sx={{ mt: 2 }} value={d.note} onChange={e => this.setDialog({ note: e.target.value })} />
                {err && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>{err}</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                    Capacity = how many units this plan carries; each server takes its profile's weight
                    {profiles.length > 0 ? ` (${profiles.map(p => `${p.id}: ${p.weight}`).join(", ")})` : ""}. Size it from a load test: the
                    servers of a full machine must keep their tick rate.
                    {editing && " Capacity and price also move to this title's machines on the plan (the price is what their spend row charges)."}
                </Typography>
            </FormDialog>
        );
    }

    render() {
        const { specs, options, loadedAt, stale } = this.state;
        const edit = canEdit();
        const columns = [
            { id: "provider", label: "Provider", width: 80, render: s => <Cut text={s.provider ?? "vultr"} /> },
            { id: "plan", label: "Plan", width: 150, render: s => <Cut text={s.plan} sx={{ fontWeight: 600 }} /> },
            { id: "hw", label: "Vultr plan", width: 190, render: s => {
                const p = this.plan(s.plan);
                return p ? <Cut text={`${p.vcpu} vCPU · ${mb(p.ramMb)} · ${p.diskGb} GB · ${p.type}`} /> : <Typography variant="body2" color="text.secondary">{options ? "not in Vultr's list" : "…"}</Typography>;
            } },
            { id: "capacity", label: "Capacity (units)", width: 90, align: "right", render: s => n(s.capacity) },
            { id: "seats", label: "Seats", width: 70, align: "right", render: s => s.seats > 0
                ? n(s.seats) : <Tooltip title="0 = derived from the title's default profile (capacity / weight × sessions × players)"><span>derived</span></Tooltip> },
            { id: "hourlyUsd", label: "Hourly price", width: 95, align: "right", render: s => s.monthlyUsd > 0
                ? <Tooltip title={`monthly $${Number(s.monthlyUsd).toFixed(2)} (reserved) → /730 for the cost view`}><span>{usdH(s.hourlyUsd)}</span></Tooltip>
                : usdH(s.hourlyUsd) },
            { id: "vultr", label: "Vultr price", width: 95, align: "right", render: s => {
                const p = this.plan(s.plan);
                return p ? <Tooltip title={`$${Number(p.monthlyUsd ?? 0).toFixed(2)}/month`}><span>{usdH(p.hourlyUsd)}</span></Tooltip> : "—";
            } },
            { id: "unit", label: "Per unit", width: 85, align: "right", render: s => s.capacity > 0 ? usdH(s.hourlyUsd / s.capacity) : "—" },
            { id: "machines", label: "Machines", width: 75, align: "right", render: s => n(s.machines) },
            { id: "note", label: "Note", width: 160, render: s => <Cut text={s.note} /> },
            updatedCol,
            ...(edit ? [actionsCol(this.openEdit, this.remove)] : []),
        ];
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Machine Specs">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    {edit && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openAdd}>Add spec</Button>}
                    <Tooltip title="Refresh"><IconButton size="small" onClick={this.loader.load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    What a Vultr plan carries for this title (capacity units; a server uses its profile's weight) and what it costs. Only a plan
                    with a spec can be deployed (Machines → Deploy machine). Changing a spec moves its capacity and price to the machines already on that plan.
                </Typography>
                {specs == null
                    ? <Typography variant="body2" color="text.secondary">{stale ? `Could not load the specs: ${stale}` : "Loading…"}</Typography>
                    : specs.length === 0
                        ? <Typography variant="body2" color="text.secondary">No spec yet: add one to be able to deploy machines.</Typography>
                        : <DataTable columns={columns} rows={specs} getRowKey={s => s.id} sx={FIXED} />}
                {this.renderDialog()}
            </PageContainer>
        );
    }
}
